/**
 * The Metals page tab *Purchases*: the metal bought from clients over a range of days
 * (`GET /api/metals/purchases?from=&to=`), each with its lines. A purchase saved without going into
 * stock can be selected and put into it (`POST /api/metals/purchases/:id/add-to-stock`).
 */
import dayjs from 'dayjs';
import { PURCHASE_PAYMENT_METHODS, type PurchaseLine } from './metalPurchase';

/** How many days back the list opens on, today included. */
export const DEFAULT_PURCHASE_LIST_DAYS = 30;

export interface PurchaseListLine extends PurchaseLine {
    lineNo: number;
}

/** A purchase as `GET /api/metals/purchases` lists it. */
export interface PurchaseListItem {
    id: number;
    purchaseDate: string;
    clientId: number;
    clientName: string;
    totalRon: number;
    /** `cash`, `bankTransfer` or `safe`; `null` when the payment was not booked. */
    paymentMethod: string | null;
    cashPointName: string | null;
    dispositionId: number | null;
    addedToStock: boolean;
    userName: string | null;
    createdAt: string;
    lines: PurchaseListLine[];
}

export interface PurchaseListRange {
    from: string;
    to: string;
}

const DATE_FORMAT = 'YYYY-MM-DD';

/** The days the list opens on: the last `days` days up to `today` (`YYYY-MM-DD`). */
export const defaultPurchaseListRange = (
    today: string,
    days = DEFAULT_PURCHASE_LIST_DAYS,
): PurchaseListRange => ({
    from: dayjs(today)
        .subtract(days - 1, 'day')
        .format(DATE_FORMAT),
    to: today,
});

export const purchaseListQueryString = (range: PurchaseListRange): string =>
    `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;

/** A purchase that can still be put into stock: not in it yet, and with something to put. */
export const canAddPurchaseToStock = (purchase: PurchaseListItem | null | undefined): boolean =>
    Boolean(purchase && !purchase.addedToStock && purchase.lines.length > 0);

/** The selected purchases that can go into stock, in the order the list shows them. */
export const purchasesToAddToStock = (
    purchases: readonly PurchaseListItem[],
    selectedIds: readonly number[],
): PurchaseListItem[] =>
    purchases.filter(purchase => selectedIds.includes(purchase.id) && canAddPurchaseToStock(purchase));

const isKnownMethod =(method: string): method is (typeof PURCHASE_PAYMENT_METHODS)[number] =>
    (PURCHASE_PAYMENT_METHODS as readonly string[]).includes(method);

/** The i18n key of how a purchase was paid: its method, or *not paid*. */
export const purchasePaymentKey = (purchase: Pick<PurchaseListItem, 'paymentMethod'>): string =>
    purchase.paymentMethod !== null && isKnownMethod(purchase.paymentMethod)
        ? `metals.purchase.methods.${purchase.paymentMethod}`
        : 'metals.purchases.notPaid';

/** The figures under the list: how many purchases, what they cost, how many wait for the stock. */
export const purchaseListSummary = (
    purchases: readonly PurchaseListItem[],
): { count: number; totalRon: number; notInStock: number } => ({
    count: purchases.length,
    totalRon: Math.round(purchases.reduce((sum, purchase) => sum + purchase.totalRon, 0)),
    notInStock: purchases.filter(purchase => !purchase.addedToStock).length,
});

/** The purchases left after the *only not in stock* filter. */
export const filterPurchases = (
    purchases: readonly PurchaseListItem[],
    onlyNotInStock: boolean,
): PurchaseListItem[] =>
    onlyNotInStock ? purchases.filter(purchase => !purchase.addedToStock) : [...purchases];
